/**
 * Contrato de entrada (Zod) de la personalización de documentos PDF. Además
 * de los límites de cada campo, valida que los textos solo usen variables
 * existentes y disponibles en su documento, para que el administrador reciba
 * un 422 con un mensaje claro en lugar de un documento con `{{…}}` visibles.
 */
import { z } from 'zod';
import { DOCUMENT_KIND_CAPABILITIES, DOCUMENT_KIND_LABELS, DOCUMENT_KINDS, DOCUMENT_SECTIONS, FONT_SCALES, LOGO_POSITIONS, PAPER_SIZES, WATERMARK_SCOPES, } from '../documents/catalog.js';
import { findTemplateIssues, templateToken } from '../documents/template.js';
import { allowedTemplateVariables } from '../documents/variables.js';
import { isValidCui, normalizeCui } from '../identity/documents.js';
import { TEXT_MAX } from './common.js';
/** Límites de la personalización de documentos. */
export const DOCUMENT_SETTINGS_LIMITS = {
    /** Líneas del encabezado (más de tres saturan el encabezado de media carta). */
    headerLinesMax: 3,
    headerLineMax: 160,
    footerTextMax: 300,
    watermarkTextMax: 30,
    titleMax: 80,
    introTextMax: TEXT_MAX.longText,
    footerNoteMax: TEXT_MAX.notes,
    /** Las cláusulas adicionales se numeran después de las 13 legales; 20 es holgado. */
    extraClausesMax: 20,
    clauseIdMax: 64,
    clauseTitleMax: 120,
    clauseBodyMax: TEXT_MAX.narrative,
    /** Firmas adicionales: más de tres no caben con las firmas legales en una fila. */
    extraSignaturesMax: 3,
    signatureLabelMax: 60,
    /** Ejemplares por impresión (original y dos copias). */
    copiesMax: 3,
    copyLabelMax: 40,
    representativeNameMax: TEXT_MAX.name,
    positionMax: TEXT_MAX.label,
    legalTextMax: TEXT_MAX.shortText,
    placeMax: TEXT_MAX.label,
    jurisdictionMax: TEXT_MAX.name,
};
/**
 * Tipos de imagen aceptados para el logotipo: el generador de PDF solo
 * dibuja PNG y JPG (SVG y WEBP no aparecerían en los documentos).
 */
export const ALLOWED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];
/** Bytes en un kilobyte. */
const BYTES_PER_KIBIBYTE = 1024;
/** Tamaño máximo del logotipo: 512 KB bastan para un logo nítido y no engordan cada PDF. */
export const MAX_LOGO_BYTES = 512 * BYTES_PER_KIBIBYTE;
/** Color hexadecimal de seis dígitos, `#1f6f5c`. */
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
/** Texto recortado con un máximo. */
const trimmedText = (max) => z.string().trim().max(max);
/** Lista de textos cortos no vacíos. */
const labelList = (itemMax, listMax) => z.array(z.string().trim().min(1, 'Escriba el rótulo').max(itemMax)).max(listMax);
/** DPI opcional: vacío o un CUI válido (se guarda sin espacios). */
const optionalCuiSchema = z
    .string()
    .trim()
    .refine((value) => value === '' || isValidCui(value), 'DPI (CUI) inválido: revisa los 13 dígitos, el departamento y el municipio')
    .transform((value) => (value === '' ? '' : normalizeCui(value)));
/** Apariencia común de los documentos. */
export const DocumentAppearanceSchema = z.object({
    accentColor: z
        .string()
        .regex(HEX_COLOR_PATTERN, 'Use un color hexadecimal de 6 dígitos, p. ej. #1f6f5c')
        .transform((value) => value.toLowerCase())
        .nullable(),
    showLogo: z.boolean(),
    logoPosition: z.enum(LOGO_POSITIONS),
    fontScale: z.enum(FONT_SCALES),
    paperSize: z.enum(PAPER_SIZES),
    headerLines: labelList(DOCUMENT_SETTINGS_LIMITS.headerLineMax, DOCUMENT_SETTINGS_LIMITS.headerLinesMax),
    footerText: trimmedText(DOCUMENT_SETTINGS_LIMITS.footerTextMax),
    showPageNumbers: z.boolean(),
    watermarkText: trimmedText(DOCUMENT_SETTINGS_LIMITS.watermarkTextMax),
    watermarkScope: z.enum(WATERMARK_SCOPES),
});
/** Datos legales de la institución para contratos y constancias. */
export const DocumentLegalDataSchema = z.object({
    representativeName: trimmedText(DOCUMENT_SETTINGS_LIMITS.representativeNameMax),
    representativeCui: optionalCuiSchema,
    representativePosition: trimmedText(DOCUMENT_SETTINGS_LIMITS.positionMax),
    representationCredential: trimmedText(DOCUMENT_SETTINGS_LIMITS.legalTextMax),
    institutionRegistration: trimmedText(DOCUMENT_SETTINGS_LIMITS.legalTextMax),
    signingPlace: trimmedText(DOCUMENT_SETTINGS_LIMITS.placeMax),
    jurisdiction: trimmedText(DOCUMENT_SETTINGS_LIMITS.jurisdictionMax),
});
/** Cláusula adicional del contrato. */
export const DocumentClauseSchema = z.object({
    id: z.string().trim().min(1).max(DOCUMENT_SETTINGS_LIMITS.clauseIdMax),
    title: z.string().trim().min(3, 'Escriba el título de la cláusula').max(DOCUMENT_SETTINGS_LIMITS.clauseTitleMax),
    body: z.string().trim().min(10, 'Escriba el texto de la cláusula').max(DOCUMENT_SETTINGS_LIMITS.clauseBodyMax),
});
/** Visibilidad de las secciones opcionales de un documento: exactamente sus claves, cada una booleana. */
function sectionsSchema(kind) {
    const keys = DOCUMENT_SECTIONS[kind];
    return z.object(Object.fromEntries(keys.map((key) => [key, z.boolean()])));
}
/** Opciones comunes de un documento. */
function templateSchema(kind) {
    return z.object({
        title: trimmedText(DOCUMENT_SETTINGS_LIMITS.titleMax),
        introText: trimmedText(DOCUMENT_SETTINGS_LIMITS.introTextMax),
        footerNote: trimmedText(DOCUMENT_SETTINGS_LIMITS.footerNoteMax),
        sections: sectionsSchema(kind),
        extraSignatures: labelList(DOCUMENT_SETTINGS_LIMITS.signatureLabelMax, DOCUMENT_SETTINGS_LIMITS.extraSignaturesMax),
        copies: labelList(DOCUMENT_SETTINGS_LIMITS.copyLabelMax, DOCUMENT_SETTINGS_LIMITS.copiesMax),
    });
}
/** Opciones de cada documento. */
export const DocumentTemplatesSchema = z.object({
    loanContract: templateSchema('loanContract').extend({
        extraClauses: z.array(DocumentClauseSchema).max(DOCUMENT_SETTINGS_LIMITS.extraClausesMax),
    }),
    paymentPlan: templateSchema('paymentPlan'),
    paymentReceipt: templateSchema('paymentReceipt'),
    accountStatement: templateSchema('accountStatement'),
    paidOffCertificate: templateSchema('paidOffCertificate'),
    disbursementVoucher: templateSchema('disbursementVoucher'),
    cashClosing: templateSchema('cashClosing'),
    tabularReport: templateSchema('tabularReport'),
});
/** Campos con variables de un documento. */
function templateFieldsOf(settings, kind) {
    const template = settings.templates[kind];
    const base = ['templates', kind];
    const fields = [
        { path: [...base, 'title'], text: template.title },
        { path: [...base, 'introText'], text: template.introText },
        { path: [...base, 'footerNote'], text: template.footerNote },
        ...template.extraSignatures.map((text, index) => ({ path: [...base, 'extraSignatures', index], text })),
        ...template.copies.map((text, index) => ({ path: [...base, 'copies', index], text })),
    ];
    if (kind === 'loanContract') {
        settings.templates.loanContract.extraClauses.forEach((clause, index) => {
            fields.push({ path: [...base, 'extraClauses', index, 'title'], text: clause.title });
            fields.push({ path: [...base, 'extraClauses', index, 'body'], text: clause.body });
        });
    }
    return fields;
}
/** Campos comunes a todos los documentos (solo admiten variables globales). */
function appearanceFieldsOf(settings) {
    const { appearance } = settings;
    return [
        ...appearance.headerLines.map((text, index) => ({ path: ['appearance', 'headerLines', index], text })),
        { path: ['appearance', 'footerText'], text: appearance.footerText },
        { path: ['appearance', 'watermarkText'], text: appearance.watermarkText },
    ];
}
/** Dónde se escribió el texto, para el mensaje de error. */
const COMMON_SCOPE_LABEL = 'el encabezado y pie comunes';
/** Mensaje en español de un problema de plantilla. */
export function templateIssueMessage(issue, scopeLabel) {
    if (issue.type === 'unknown') {
        return `La variable ${templateToken(issue.variable)} no existe o no está disponible en ${scopeLabel}. Use la ayuda de variables para insertarla.`;
    }
    return `Revise las llaves cerca de «${issue.fragment}»: cada variable se escribe así: ${templateToken('cliente.nombre')}`;
}
/** Revisa las variables de todos los textos y agrega un error por campo con problemas. */
function refineTemplates(settings, ctx) {
    const check = (fields, allowed, scopeLabel) => {
        for (const field of fields) {
            for (const issue of findTemplateIssues(field.text, allowed)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: field.path, message: templateIssueMessage(issue, scopeLabel) });
            }
        }
    };
    check(appearanceFieldsOf(settings), allowedTemplateVariables(), COMMON_SCOPE_LABEL);
    for (const kind of DOCUMENT_KINDS) {
        check(templateFieldsOf(settings, kind), allowedTemplateVariables(kind), `«${DOCUMENT_KIND_LABELS[kind]}»`);
        if (!DOCUMENT_KIND_CAPABILITIES[kind].customTitle && settings.templates[kind].title !== '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['templates', kind, 'title'],
                message: `El título de «${DOCUMENT_KIND_LABELS[kind]}» no se personaliza: cada reporte usa el suyo`,
            });
        }
    }
}
/** Los identificadores de las cláusulas adicionales no se repiten (se usan para ordenar). */
function refineClauseIds(settings, ctx) {
    const seen = new Set();
    settings.templates.loanContract.extraClauses.forEach((clause, index) => {
        if (seen.has(clause.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['templates', 'loanContract', 'extraClauses', index, 'id'],
                message: 'Identificador de cláusula repetido',
            });
        }
        seen.add(clause.id);
    });
}
/** Personalización completa de los documentos, con validación de variables. */
export const DocumentSettingsSchema = z
    .object({
    appearance: DocumentAppearanceSchema,
    legal: DocumentLegalDataSchema,
    templates: DocumentTemplatesSchema,
})
    .superRefine((settings, ctx) => {
    refineTemplates(settings, ctx);
    refineClauseIds(settings, ctx);
});
//# sourceMappingURL=documents.js.map